import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  AirportShuttle as TransferIcon,
  Backpack as EquipmentIcon,
  DirectionsBoat as YachtIcon,
  DirectionsCar as CarIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  Flight as FlightIcon,
  Home as VillaIcon,
  LocalActivity as ExperienceIcon,
  MoreHoriz as OtherIcon,
  Restaurant as RestaurantIcon,
  Search as SearchIcon,
  Spa as WellnessIcon,
  Upgrade as UpgradeIcon,
  VerifiedUser as InsuranceIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx } from './PartnerListChrome';
import InventoryItemDetails from './InventoryItemDetails';
import PartnerTripDeliveryOptions from './PartnerTripDeliveryOptions';
import { formatInventoryPriceLabel } from '../utils/inventoryPrice';
import { isUsableImageUrl } from '../utils/itineraryItemImage';

const categoryIcon = (category) => {
  switch (category) {
    case 'car':
      return CarIcon;
    case 'yacht':
      return YachtIcon;
    case 'villa':
      return VillaIcon;
    case 'aviation':
      return FlightIcon;
    case 'experience':
      return ExperienceIcon;
    case 'restaurant':
    case 'dining':
      return RestaurantIcon;
    case 'event':
      return EventIcon;
    case 'transfer':
      return TransferIcon;
    case 'wellness':
      return WellnessIcon;
    case 'upgrade':
      return UpgradeIcon;
    case 'insurance':
      return InsuranceIcon;
    case 'equipment':
      return EquipmentIcon;
    default:
      return OtherIcon;
  }
};

/**
 * Searchable scrolling inventory / add-on picker with expandable details and
 * free / charge pricing. Pass onDeliveryModeChange to also choose whether the
 * item goes on the traveler's bill ('charge') or waits for them to buy it ('offer').
 */
const PartnerInventoryPickerDialog = ({
  open,
  onClose,
  items = [],
  selectedId = '',
  onSelect,
  onConfirm,
  confirming = false,
  title,
  confirmLabel,
  emptyLabel,
  showQuantity = false,
  quantity = '1',
  onQuantityChange,
  pricingMode = 'charge',
  onPricingModeChange,
  deliveryMode = 'charge',
  onDeliveryModeChange,
  categoryKeyPrefix = 'partnerInventory.category',
  searchPlaceholder,
  testIdPrefix = 'inventory-picker',
  initialQuery = '',
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState('');

  useEffect(() => {
    if (open) {
      setQuery(initialQuery || '');
      setExpandedId('');
    }
  }, [open, initialQuery]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => (
      [item.name, item.description, item.category, item.notes]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    ));
  }, [items, query]);

  const selected = useMemo(
    () => items.find((item) => String(item.id) === String(selectedId)),
    [items, selectedId]
  );

  const handleClose = () => {
    setQuery('');
    setExpandedId('');
    onClose?.();
  };

  const handleItemClick = (itemId) => {
    const id = String(itemId);
    onSelect?.(id);
    setExpandedId((prev) => (prev === id ? '' : id));
  };

  const imageSrc = (item) => item.imageUrl || item.image_url || '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: DOCUMENT.surface,
          borderRadius: 0,
          maxHeight: '92vh',
          width: '100%',
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, pb: 1 }}>
        {title}
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          minHeight: { xs: 420, sm: 520 },
        }}
      >
        {items.length === 0 ? (
          <Typography sx={{ color: DOCUMENT.ink3, py: 2 }}>{emptyLabel}</Typography>
        ) : (
          <>
            <TextField
              fullWidth
              variant="standard"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder || t('partnerInventory.pickerSearch')}
              inputProps={{
                'aria-label': searchPlaceholder || t('partnerInventory.pickerSearch'),
                'data-testid': `${testIdPrefix}-search`,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: DOCUMENT.ink3 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
                '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
              }}
            />

            <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: -0.5 }}>
              {t('partnerInventory.clickToExpand')}
            </Typography>

            <Box
              role="listbox"
              aria-label={t('partnerInventory.title')}
              data-testid={`${testIdPrefix}-list`}
              sx={{
                flex: 1,
                minHeight: 260,
                maxHeight: { xs: '42vh', sm: '52vh' },
                overflowY: 'auto',
                borderTop: `1px solid ${DOCUMENT.ruleHard}`,
                borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
              }}
            >
              {filtered.length === 0 ? (
                <Typography sx={{ color: DOCUMENT.ink3, py: 3, px: 0.5 }}>
                  {t('partnerInventory.noSearchResults')}
                </Typography>
              ) : (
                filtered.map((item) => {
                  const id = String(item.id);
                  const isSelected = id === String(selectedId);
                  const isExpanded = id === expandedId;
                  const src = imageSrc(item);
                  const Icon = categoryIcon(item.category);
                  const hasImage = isUsableImageUrl(src);

                  return (
                    <Box
                      key={item.id}
                      sx={{
                        borderBottom: `1px solid ${DOCUMENT.rule}`,
                        bgcolor: isSelected
                          ? `color-mix(in srgb, ${DOCUMENT.oxblood} 8%, transparent)`
                          : 'transparent',
                        boxShadow: isSelected ? `inset 3px 0 0 ${DOCUMENT.oxblood}` : 'none',
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        data-testid={`${testIdPrefix}-item-${item.id}`}
                        onClick={() => handleItemClick(item.id)}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '64px 1fr 28px', sm: '88px 1fr 28px' },
                          gap: 1.5,
                          width: '100%',
                          textAlign: 'left',
                          alignItems: 'center',
                          py: 1.5,
                          px: 1,
                          m: 0,
                          border: 0,
                          cursor: 'pointer',
                          bgcolor: 'transparent',
                          font: 'inherit',
                          color: DOCUMENT.ink,
                          '&:hover': {
                            bgcolor: `color-mix(in srgb, ${DOCUMENT.ink} 4%, transparent)`,
                          },
                        }}
                      >
                        {hasImage ? (
                          <Box
                            component="img"
                            src={src}
                            alt=""
                            sx={{
                              width: { xs: 64, sm: 88 },
                              height: { xs: 48, sm: 66 },
                              objectFit: 'cover',
                              bgcolor: DOCUMENT.rule,
                              display: 'block',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: { xs: 64, sm: 88 },
                              height: { xs: 48, sm: 66 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: DOCUMENT.rule,
                              color: DOCUMENT.ink3,
                            }}
                          >
                            <Icon sx={{ fontSize: 26 }} aria-hidden />
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 16, lineHeight: 1.25, fontFamily: DOCUMENT.display }}>
                            {item.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: DOCUMENT.ink3,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              mt: 0.4,
                            }}
                          >
                            {t(`${categoryKeyPrefix}.${item.category || 'other'}`)}
                            {' · '}
                            {formatInventoryPriceLabel(item, t)}
                          </Typography>
                        </Box>
                        <ExpandMoreIcon
                          aria-hidden
                          sx={{
                            fontSize: 22,
                            color: DOCUMENT.ink3,
                            justifySelf: 'end',
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </Box>

                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <InventoryItemDetails
                          item={item}
                          testId={`${testIdPrefix}-item-${item.id}-details`}
                        />
                      </Collapse>
                    </Box>
                  );
                })
              )}
            </Box>

            <PartnerTripDeliveryOptions
              item={selected}
              pricingMode={pricingMode}
              onPricingModeChange={onPricingModeChange}
              deliveryMode={deliveryMode}
              onDeliveryModeChange={onDeliveryModeChange}
              showQuantity={showQuantity}
              quantity={quantity}
              onQuantityChange={onQuantityChange}
              testIdPrefix={testIdPrefix}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          sx={{
            minHeight: 32,
            height: 32,
            px: 1.5,
            py: 0.5,
            fontSize: 11,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: DOCUMENT.oxblood,
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          sx={{
            ...partnerOxbloodBtnSx,
            minHeight: 32,
            height: 32,
            px: 2,
            py: 0.5,
            fontSize: 11,
          }}
          disabled={confirming || !selectedId || items.length === 0}
          onClick={onConfirm}
          data-testid={`${testIdPrefix}-confirm`}
        >
          {confirming ? t('common.saving') : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerInventoryPickerDialog;
